import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TableFooter,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  TablePagination,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Snackbar,
  Stack,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  AccountBalance,
  Warning,
  ArrowForward,
  Visibility,
  CheckCircle,
  AttachMoney,
  Description,
  AttachFile,
  ZoomIn,
  FilterList,
  Download,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  Print,
  Share,
  Refresh,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  getAllCustomers2,
  getCustomersWithPendingTransactions,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonPage, IonHeader, IonToolbar, IonTitle } from "@ionic/react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { fetchSalesData_withdate_staffId_paymentMethodCall, getManagersOrders_reciept, getManagersOrders_reciept_withdate, getOrderTransaction, verifyTransaction } from "../../../api/reports/reports";
import { getStaffList_saleapp } from "../../../api/users/user";
import { baseUrl } from "../../../common/utils";

// Import ReceiptModal component
import ReceiptModal_giftcard_crypto from "../../../components/sales/ReceiptModal_giftcard_crypto";
import { MenuButton } from "../../../components/Menu";

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

interface PendingTransactionSummary {
  customer_id: string;
  total_outstanding: number;
  transaction_count: number;
  customer: Customer;
  member_name: string;
  customer_phone: string;
  customer_email: string;
  balance_due: number;
  // ADD TRANSACTION FIELDS HERE
  order_id?: string;
  order_code?: string;
  order_date?: string;
  mode_of_payment?: string;
  payable_amount?: string;
  amount_paid?: string;
  order_status?: string;
  product_id_type?: string;
  is_verified?: number;
  is_audited?: number;
  // Additional fields for verification dialog
  customer_name?: string;
  customer_account_name?: string;
  customer_account_number?: string;
  bank_name?: string;
  active_status?: string;
  staff_name?: string;
  mode_of_payment_real?: string;
  card_amount?: string;
  rmbValue?: string;
  cryptoType?: string;
  cryptoAmount?: string;
  costPrice?: string;
  sellingPrice?: string;
  commission?: string;
  external_processor?: string;
  processor_type?: string;
  processor_category?: string;
  load_type?: string;
  card_rate?: string;
  naira_rate?: string;
  internal_rate?: string;
  created_at?: string;
  last_updated?: string;
  proof_of_payment?: string;
  proof_of_jobstart?: string;
  crypto_value?: string;
  images?: string[];
  screenshot?: string;
  transaction_proof?: string;
  // Added for receipt
  giftcardType?: string;
  cardValue?: string;
  payment_status?: number;
  member_id?: string;
  order_by?: string;
  cardUnit?: string;
}

interface OrderItem {
  product_id: string;
  quantity: string;
  product_name: string;
  price: string;
  total_amount: string;
  item_status: string;
}

interface Staff {
  staff_id: string;
  staff_name: string;
  user_id: string;
  firstName: string;
  lastName: string;
}

interface FilterState {
  startDate: Date | null;
  endDate: Date | null;
  paymentMethod: string;
  department: string;
  orderStatus: string;
  paymentStatus: string;
  staffId: string;
  productType: string;
}

const CustomerAccountPage_giftcard_crypto_pending: React.FC = () => {
  const history = useHistory();
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransactionSummary[]>([]);
  const [filteredPending, setFilteredPending] = useState<PendingTransactionSummary[]>([]);
  const [loading, setLoading] = useState({
    customers: false,
    pending: false,
  });
  const [searchQuery, setSearchQuery] = useState("");

  const [showFilters, setShowFilters] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [transactionToVerify, setTransactionToVerify] = useState<PendingTransactionSummary | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyDialogLoading, setVerifyDialogLoading] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    startDate: dayjs().toDate(),
    endDate: dayjs().toDate(),
    paymentMethod: "",
    department: "",
    orderStatus: "",
    paymentStatus: "",
    staffId: "",
    productType: "",
  });

  const autoRefreshEnabledRef = useRef(autoRefreshEnabled);
  const verifyDialogOpenRef = useRef(verifyDialogOpen);
  const receiptOpenRef = useRef(receiptOpen);
  
  useEffect(() => {
    autoRefreshEnabledRef.current = autoRefreshEnabled;
    verifyDialogOpenRef.current = verifyDialogOpen;
    receiptOpenRef.current = receiptOpen;
  }, [autoRefreshEnabled, verifyDialogOpen, receiptOpen]);

  useEffect(() => {
    fetchAllCustomers();
    fetchPendingTransactions();
    fetchStaffData();
    
    const intervalId = setInterval(() => {
      if (autoRefreshEnabledRef.current && !verifyDialogOpenRef.current && !receiptOpenRef.current) {
        fetchPendingTransactionsQuietly();
      }
    }, 8000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, filters, pendingTransactions]);

  const fetchAllCustomers = async () => {
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await getAllCustomers2();
      setAllCustomers(response.data);
    } catch (error) {
      console.error("Error fetching all customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  const fetchPendingTransactions = async () => {
    try {
      setLoading((prev) => ({ ...prev, pending: true }));
      const response = await getCustomersWithPendingTransactions();
      const transformedData = (response.data.orders || []).map((item: any) => ({
        ...item,
        order_id: item.order_id || item.transaction_id || "",
        order_code: item.order_code || "N/A",
        order_date: item.order_date || item.created_at || "",
        mode_of_payment: item.mode_of_payment || "N/A",
        payable_amount: item.payable_amount || "0",
        amount_paid: item.amount_paid || "0",
        order_status: item.order_status || "PENDING",
        product_id_type: item.product_id_type || "N/A",
        is_verified: item.is_verified || 0,
        is_audited: item.is_audited || 0,
        customer_name: item.member_name || "",
        customer_phone: item.customer_phone || "",
        customer_email: item.customer_email || "",
      }));
      
      setPendingTransactions(transformedData);
      setFilteredPending(transformedData);
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error("Error fetching pending transactions:", error);
    } finally {
      setLoading((prev) => ({ ...prev, pending: false }));
    }
  };

  const fetchPendingTransactionsQuietly = async () => {
    if (isAutoRefreshing || verifyDialogOpenRef.current || receiptOpenRef.current) return;
    
    try {
      setIsAutoRefreshing(true);
      const response = await getCustomersWithPendingTransactions();
      const transformedData = (response.data.orders || []).map((item: any) => ({
        ...item,
        order_id: item.order_id || item.transaction_id || "",
        order_code: item.order_code || "N/A",
        order_date: item.order_date || item.created_at || "",
        mode_of_payment: item.mode_of_payment || "N/A",
        payable_amount: item.payable_amount || "0",
        amount_paid: item.amount_paid || "0",
        order_status: item.order_status || "PENDING",
        product_id_type: item.product_id_type || "N/A",
        is_verified: item.is_verified || 0,
        is_audited: item.is_audited || 0,
        customer_name: item.member_name || "",
        customer_phone: item.customer_phone || "",
        customer_email: item.customer_email || "",
      }));
      
      setPendingTransactions(transformedData);
      setFilteredPending(transformedData);
      setLastRefreshTime(new Date());
      setRefreshCount(prev => prev + 1);
    } catch (error) {
      console.error("Error during auto-refresh:", error);
    } finally {
      setIsAutoRefreshing(false);
    }
  };

  const fetchStaffData = async () => {
    try {
      const response = await getStaffList_saleapp();
      if (response.data) {
        setStaffList(response.data);
      }
    } catch (error) {
      console.error("Error fetching staff data:", error);
    }
  };

  const applyFilters = () => {
    let filtered = pendingTransactions || [];

    if (searchQuery) {
      filtered = filtered?.filter(
        (transaction) =>
          transaction?.order_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction?.member_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction?.customer_phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction?.customer_email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered?.filter((transaction) => {
        if (!transaction?.order_date) return false;
        const transactionDate = new Date(transaction.order_date);
        return transactionDate >= start && transactionDate <= end;
      });
    }

    if (filters.paymentMethod && filters.paymentMethod !== "All") {
      filtered = filtered?.filter(
        (transaction) => transaction?.mode_of_payment === filters.paymentMethod
      );
    }

    if (filters.orderStatus && filters.orderStatus !== "All") {
      filtered = filtered?.filter((transaction) => transaction?.order_status === filters.orderStatus);
    }

    if (filters.staffId && filters.staffId !== "All") {
      filtered = filtered?.filter((transaction) => transaction?.order_by === filters.staffId);
    }

    if (filters.productType && filters.productType !== "All") {
      filtered = filtered?.filter((transaction) => transaction?.product_id_type === filters.productType);
    }

    setFilteredPending(filtered);
  };

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: dayjs().toDate(),
      endDate: dayjs().toDate(),
      paymentMethod: "",
      department: "",
      orderStatus: "",
      paymentStatus: "",
      staffId: "",
      productType: "",
    });
    setSearchQuery("");
  };

  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(prev => !prev);
  };

  const handleManualRefresh = () => {
    fetchPendingTransactions();
  };

  const handleVerifyClick = async (transaction: PendingTransactionSummary) => {
    setVerifyDialogOpen(true);
    setVerifyDialogLoading(true);

    try {
      const transactionToFetch = {
        order_code: transaction.order_code,
        order_id: transaction.order_id,
        member_id: transaction.customer_id,
      };
      
      const response = await getOrderTransaction(transactionToFetch);
      if (response.status) {
        setTransactionToVerify({
          ...transaction,
          ...response.data
        });
      } else {
        setTransactionToVerify(transaction);
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      setTransactionToVerify(transaction);
    } finally {
      setTimeout(() => {
        setVerifyDialogLoading(false);
      }, 500);
    }
  };

  const handleVerifyTransaction = async () => {
    if (!transactionToVerify) return;
    setIsVerifying(true);
    try {
      await verifyTransaction(transactionToVerify);
      fetchPendingTransactions();
    } catch (error) {
      console.error("Error verifying transaction:", error);
      alert("An error occurred during verification.");
    } finally {
      setIsVerifying(false);
      setVerifyDialogOpen(false);
      setTransactionToVerify(null);
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    return `${Math.floor(diffSec / 3600)}h ago`;
  };

  const handleViewReceipt = (transaction: PendingTransactionSummary) => {
    if (!transaction) return;

    const receiptItems = [{
      orderID: transaction.order_id,
      productName: transaction.product_id_type || 'Unknown Product',
      quantity: transaction.card_amount || '0',
      unitPrice: parseFloat(transaction.sellingPrice || "0") || 0,
      amount: parseFloat(transaction.sellingPrice || "0") || 0,
      status: transaction.order_status || 'COMPLETED',
      product_id_type: transaction.product_id_type || 'N/A',
      cryptoType: transaction.cryptoType || 'N/A',
      cryptoAmount: transaction.cryptoAmount || 'N/A',
      giftcardType: transaction.giftcardType || 'N/A',
      cardValue: transaction.cardValue || 'N/A',
    }];

    const totalAmount = parseFloat(transaction.payable_amount || "0");
    const balanceDue = parseFloat(transaction.balance_due?.toString() || "0");
    const paidAmount = totalAmount - balanceDue;

    setReceiptData({
      customer: {
        name: transaction.customer_name || transaction.member_name || 'N/A',
        phone: transaction.customer_phone || 'N/A',
        email: transaction.customer_email || 'N/A',
      },
      order: {
        id: transaction.order_code || 'N/A',
        date: transaction.order_date || new Date().toISOString(),
        status: transaction.order_status || 'COMPLETED',
        paymentStatus: transaction.payment_status === 1 ? '1' : '0',
        paymentMethod: transaction.mode_of_payment || 'cash',
        customer_account_name: transaction.customer_account_name || 'N/A',
        customer_account_number: transaction.customer_account_number || 'N/A',
        bank_name: transaction.bank_name || 'N/A'
      },
      items: receiptItems,
      summary: {
        totalAmount: totalAmount,
        paidAmount: paidAmount,
        balanceDue: balanceDue
      },
      payment: {
        amount: paidAmount,
        method: transaction.mode_of_payment || 'cash',
        date: transaction.order_date || new Date().toISOString(),
        notes: `Payment for order ${transaction.order_code}`
      }
    });
    setReceiptOpen(true);
  };

  const handleCopyReceipt = async () => {
    if (!receiptData) return;
    try {
      const receiptText = formatReceiptForWhatsApp(receiptData);
      await navigator.clipboard.writeText(receiptText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error("Failed to copy receipt:", error);
    }
  };

  const formatReceiptForWhatsApp = (receipt: any) => {
    const lines = [];
    lines.push("💰 *PAYMENT RECEIPT* 💰");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push("");
    lines.push("*Customer Information:*");
    lines.push(`Name: ${receipt.customer.name}`);
    lines.push(`Phone: ${receipt.customer.phone}`);
    lines.push(`Email: ${receipt.customer.email}`);
    lines.push("");
    lines.push("*Order Information:*");
    lines.push(`Order ID: ${receipt.order.id}`);
    lines.push(`Date: ${new Date(receipt.order.date).toLocaleDateString()}`);
    lines.push(`Status: ${receipt.order.status}`);
    lines.push("");
    lines.push("*Payment Details:*");
    lines.push(`Method: ${receipt.order.paymentMethod}`);
    lines.push(`Amount Paid: ${formatCurrencyN(receipt.payment.amount)}`);
    lines.push(`Payment Date: ${new Date(receipt.payment.date).toLocaleDateString()}`);
    lines.push("");
    lines.push("*Account Details:*");
    lines.push(`Account Name: ${receipt.order.customer_account_name}`);
    lines.push(`Account Number: ${receipt.order.customer_account_number}`);
    lines.push(`Bank: ${receipt.order.bank_name}`);
    lines.push("");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push("*Transaction Summary:*");
    lines.push(`Total Amount: ${formatCurrencyN(receipt.summary.totalAmount)}`);
    lines.push(`Amount Paid: ${formatCurrencyN(receipt.summary.paidAmount)}`);
    lines.push(`Balance Due: ${formatCurrencyN(receipt.summary.balanceDue)}`);
    lines.push("");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push("✅ *Payment Confirmed* ✅");
    lines.push("Thank you for your business!");
    return lines.join("\n");
  };

  const getOrderStatusColor = (status: string) => {
    if (!status) return "default";
    switch (status.toUpperCase()) {
      case "COMPLETED": return "success";
      case "PENDING": return "warning";
      case "CANCELLED": return "error";
      default: return "default";
    }
  };

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount;
    if (isNaN(numAmount)) return "₦0.00";
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-NG", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const getOutstandingColor = (amount: number) => {
    if (amount > 50000) return "error";
    if (amount > 20000) return "warning";
    return "primary";
  };

  const totalOutstanding = filteredPending.reduce(
    (sum, item) => sum + Number(item.balance_due || 0),
    0
  );
  const totalCustomers = filteredPending.length;

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <MenuButton />
          <IonTitle>Pending Transactions</IonTitle>
        </IonToolbar>
      </IonHeader>
      
      <IonContent>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack 
            direction={{ xs: "column", sm: "row" }} 
            justifyContent="space-between" 
            alignItems={{ xs: "flex-start", sm: "center" }} 
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" }, fontWeight: "bold" }}>
              Pending Transactions
            </Typography>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 }, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {autoRefreshEnabled && <CircularProgress size={12} />}
                <Typography variant="caption" color="text.secondary">
                  Last updated: {formatTimeAgo(lastRefreshTime)}
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title={autoRefreshEnabled ? "Pause" : "Start"}>
                  <IconButton size="small" onClick={toggleAutoRefresh} color={autoRefreshEnabled ? "primary" : "default"}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Button variant="outlined" size="small" onClick={handleManualRefresh} disabled={loading.pending} startIcon={loading.pending ? <CircularProgress size={16} /> : <Refresh />}>
                  Refresh
                </Button>
              </Stack>
            </Box>
          </Stack>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: "warning.light", borderRadius: 3 }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Warning sx={{ fontSize: 32, mb: 0.5 }} />
                  <Typography variant="subtitle2">Total Outstanding</Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {formatCurrencyN(totalOutstanding)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Card sx={{ bgcolor: "success.light", borderRadius: 3 }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Person sx={{ fontSize: 32, mb: 0.5 }} />
                  <Typography variant="subtitle2">Total Pending</Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {totalCustomers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Card sx={{ bgcolor: "info.light", borderRadius: 3 }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Refresh sx={{ fontSize: 32, mb: 0.5 }} />
                  <Typography variant="subtitle2">Auto-Refresh</Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {autoRefreshEnabled ? "ON" : "OFF"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Filters */}
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Search order, name, phone..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                }}
              />
              <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  fullWidth
                >
                  Filters
                </Button>
                {showFilters && (
                  <Button variant="text" color="secondary" onClick={clearFilters}>
                    Clear
                  </Button>
                )}
              </Stack>
            </Stack>

            {showFilters && (
              <Box sx={{ borderTop: 1, borderColor: "divider", mt: 2, pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker label="Start Date" value={dayjs(filters.startDate)} onChange={(d) => handleFilterChange("startDate", d?.toDate())} renderInput={(params) => <TextField {...params} size="small" fullWidth />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker label="End Date" value={dayjs(filters.endDate)} onChange={(d) => handleFilterChange("endDate", d?.toDate())} renderInput={(params) => <TextField {...params} size="small" fullWidth />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Product Type</InputLabel>
                      <Select value={filters.productType} label="Product Type" onChange={(e) => handleFilterChange("productType", e.target.value)}>
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="GiftCard">Gift Card</MenuItem>
                        <MenuItem value="Crypto">Crypto</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Results View */}
          {loading.pending && filteredPending.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Mobile Card List View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                {filteredPending.length === 0 ? (
                  <Typography align="center" sx={{ py: 4 }} color="text.secondary">No transactions found</Typography>
                ) : (
                  filteredPending.map((transaction) => (
                    <Card key={transaction.order_id || transaction.order_code} sx={{ mb: 2, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                              {transaction.order_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(transaction.order_date || "")}
                            </Typography>
                          </Box>
                          <Chip label={transaction.product_id_type} size="small" color={transaction.product_id_type === "GiftCard" ? "primary" : "secondary"} />
                        </Stack>
                        
                        <Typography variant="body2" sx={{ fontWeight: "medium", mb: 0.5 }}>
                          {transaction.member_name}
                        </Typography>
                        
                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                          <Chip label={transaction.mode_of_payment} size="small" variant="outlined" />
                          <Chip label={transaction.order_status} size="small" color={getOrderStatusColor(transaction.order_status) as any} />
                        </Stack>
                        
                        <Divider sx={{ mb: 1.5 }} />
                        
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "bold", color: getOutstandingColor(Number(transaction.balance_due || 0)) }}>
                              {formatCurrencyN(Number(transaction.balance_due))}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" color="primary" onClick={() => handleViewReceipt(transaction)}><Receipt /></IconButton>
                            <IconButton size="small" color="info" onClick={() => handleVerifyClick(transaction)}><Visibility /></IconButton>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>

              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                  <Table sx={{ minWidth: 1000 }}>
                    <TableHead sx={{ bgcolor: "grey.50" }}>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Order Info</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Amount Paid</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPending.map((transaction) => (
                        <TableRow key={transaction.order_id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Avatar sx={{ bgcolor: "primary.light" }}><Person /></Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>{transaction.member_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{transaction.customer_phone}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>{transaction.order_code}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDate(transaction.order_date || "")}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={transaction.product_id_type} size="small" color={transaction.product_id_type === "GiftCard" ? "primary" : "secondary"} />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>{transaction.mode_of_payment}</Typography>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(transaction.amount_paid || 0)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: "bold", color: getOutstandingColor(Number(transaction.balance_due || 0)) }}>
                              {formatCurrencyN(Number(transaction.balance_due))}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={transaction.order_status} size="small" color={getOrderStatusColor(transaction.order_status) as any} />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="View Receipt"><IconButton size="small" onClick={() => handleViewReceipt(transaction)}><Receipt /></IconButton></Tooltip>
                              <Tooltip title="Verify Transaction"><IconButton size="small" onClick={() => handleVerifyClick(transaction)} color="info"><Visibility /></IconButton></Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter sx={{ bgcolor: "grey.100" }}>
                      <TableRow>
                        <TableCell colSpan={4} align="right"><Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>Totals</Typography></TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "error.main" }}>
                            {formatCurrencyN(totalOutstanding)}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>

        {/* Verification Dialog */}
        <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ bgcolor: "primary.main", color: "white", py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Transaction Details</Typography>
              <IconButton size="small" onClick={() => setVerifyDialogOpen(false)} sx={{ color: "white" }}><Close /></IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {verifyDialogLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
            ) : transactionToVerify && (
              <Box sx={{ py: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Order Info</Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Order Code:</Typography><Typography variant="body2" fontWeight="bold">{transactionToVerify.order_code}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Date:</Typography><Typography variant="body2">{formatDate(transactionToVerify.order_date || "")}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Status:</Typography><Chip label={transactionToVerify.order_status} size="small" color={getOrderStatusColor(transactionToVerify.order_status) as any} /></Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Payment Summary</Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Selling Price:</Typography><Typography variant="body2" fontWeight="bold">{formatCurrency(transactionToVerify.sellingPrice || 0)}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Cost Price:</Typography><Typography variant="body2">{formatCurrency(transactionToVerify.costPrice || 0)}</Typography></Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between"><Typography variant="subtitle2" color="error.main">Outstanding:</Typography><Typography variant="subtitle2" fontWeight="bold" color="error.main">{formatCurrencyN(Number(transactionToVerify.balance_due || 0))}</Typography></Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Verification Proofs</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, border: 1, borderColor: "divider", borderRadius: 2 }}>
                          <Typography variant="caption" display="block" gutterBottom>Payment Proof</Typography>
                          <img 
                            src={`${baseUrl()}assets/img/gift_card/${transactionToVerify.proof_of_payment || "zillyuser.jpg"}`} 
                            style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                            onClick={() => handleImageClick(`assets/img/gift_card/${transactionToVerify.proof_of_payment || "zillyuser.jpg"}`)}
                            alt="Payment Proof"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, border: 1, borderColor: "divider", borderRadius: 2 }}>
                          <Typography variant="caption" display="block" gutterBottom>Job Start Proof</Typography>
                          <img 
                            src={`${baseUrl()}assets/img/gift_card_crypto_start/${transactionToVerify.proof_of_jobstart || "zillyuser.jpg"}`} 
                            style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                            onClick={() => handleImageClick(`assets/img/gift_card_crypto_start/${transactionToVerify.proof_of_jobstart || "zillyuser.jpg"}`)}
                            alt="Job Start Proof"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setVerifyDialogOpen(false)} variant="outlined">Close</Button>
            <Button variant="outlined" color="primary" onClick={() => handleViewReceipt(transactionToVerify!)}>View Receipt</Button>
            {transactionToVerify && Number(transactionToVerify.is_verified) === 0 && (
              <Button 
                variant="contained" 
                color="success" 
                onClick={handleVerifyTransaction}
                disabled={isVerifying}
                startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              >
                {isVerifying ? "Verifying..." : "Verify Transaction"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Image Preview */}
        <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md">
          <Box sx={{ position: "relative" }}>
            <IconButton onClick={() => setImageDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8, bgcolor: "rgba(0,0,0,0.5)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}><Close /></IconButton>
            <img src={`${baseUrl()}${selectedImage}`} style={{ width: "100%", height: "auto", display: "block" }} alt="Full preview" />
          </Box>
        </Dialog>

        <Snackbar open={copySuccess} autoHideDuration={3000} onClose={() => setCopySuccess(false)} message="Receipt copied to clipboard!" />
        
        {receiptData && <ReceiptModal_giftcard_crypto open={receiptOpen} onClose={() => setReceiptOpen(false)} receiptData={receiptData} onCopy={handleCopyReceipt} />}
      </IonContent>
    </IonPage>
  );
};

export default CustomerAccountPage_giftcard_crypto_pending;
